import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import axios from 'axios';

const Header = () => {
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [apiCategories, setApiCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [subcategories, setSubcategories] = useState({});
  const [loadingSubcategories, setLoadingSubcategories] = useState({});
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [favoritesCount, setFavoritesCount] = useState(3);
  const [cartCount, setCartCount] = useState(0);
  const [auctionCount, setAuctionCount] = useState(0);
  const [userData, setUserData] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();

  const api = axios.create({
    baseURL: 'https://api.autocasses.com/api',
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });

  useEffect(() => {
    checkAuthStatus();
    fetchCategories();
    
    // Simuler des données pour les badges
    setCartCount(3);
    setAuctionCount(5);
    
    // Écouter les changements de localStorage
    window.addEventListener('storage', checkAuthStatus);
    // Écouter les événements personnalisés pour les mises à jour d'authentification
    window.addEventListener('auth-change', checkAuthStatus);
    
    return () => {
      window.removeEventListener('storage', checkAuthStatus);
      window.removeEventListener('auth-change', checkAuthStatus);
    };
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Vérifier le même token que dans votre AuthScreen
      const token = localStorage.getItem('userToken');
      const userDataStr = localStorage.getItem('userData');
      
      if (token && userDataStr) {
        setIsAuthenticated(true);
        setUserData(JSON.parse(userDataStr));
      } else {
        setIsAuthenticated(false);
        setUserData(null);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setIsAuthenticated(false);
      setUserData(null);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await api.get('/categories/all');
      
      const transformedCategories = response.data.map((category) => {
        const categoryName = category.nom.toLowerCase();
        let icon = 'Car';
        
        if (categoryName.includes('moto')) icon = 'Motorcycle';
        else if (categoryName.includes('moteur')) icon = 'Engine';
        else if (categoryName.includes('vehicule') || categoryName.includes('voiture')) icon = 'Car';
        else if (categoryName.includes('lubrifiant') || categoryName.includes('huile')) icon = 'Droplet';
        else if (categoryName.includes('art')) icon = 'Palette';
        else if (categoryName.includes('électrique') || categoryName.includes('electronique')) icon = 'Zap';
        else if (categoryName.includes('frein')) icon = 'Brakes';
        else if (categoryName.includes('suspension')) icon = 'Car';
        else if (categoryName.includes('transmission')) icon = 'Cogs';
        else if (categoryName.includes('carrosserie')) icon = 'Car';
        else if (categoryName.includes('échappement')) icon = 'Car';
        
        return {
          id: category.id.toString(),
          originalId: category.id,
          label: category.nom,
          icon: icon,
          iconType: 'fontawesome',
          description: category.description || 'Description générique',
          photo_url: category.photo_url,
          subcategories: []
        };
      });
      
      setApiCategories(transformedCategories);
      
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
      
      const fallbackCategories = [
        { 
          id: '1', 
          originalId: 1,
          label: 'Moteur', 
          icon: 'Engine',
          iconType: 'fontawesome',
          description: 'Pièces de moteur et accessoires',
          photo_url: null,
          subcategories: []
        },
        { 
          id: '2', 
          originalId: 2,
          label: 'Transmission', 
          icon: 'Cogs',
          iconType: 'fontawesome',
          description: 'Boîtes de vitesse et transmission',
          photo_url: null,
          subcategories: []
        },
        { 
          id: '3', 
          originalId: 3,
          label: 'Freins', 
          icon: 'Brakes',
          iconType: 'fontawesome',
          description: 'Systèmes de freinage',
          photo_url: null,
          subcategories: []
        },
        { 
          id: '4', 
          originalId: 4,
          label: 'Suspension', 
          icon: 'Car',
          iconType: 'fontawesome',
          description: 'Suspensions et amortisseurs',
          photo_url: null,
          subcategories: []
        },
        { 
          id: '5', 
          originalId: 5,
          label: 'Carrosserie', 
          icon: 'Car',
          iconType: 'fontawesome',
          description: 'Pièces de carrosserie',
          photo_url: null,
          subcategories: []
        },
      ];
      setApiCategories(fallbackCategories);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      setLoadingSubcategories(prev => ({ ...prev, [categoryId]: true }));
      
      const response = await api.get(`https://api.autocasses.com/api/souscat/by-category/${categoryId}`);
      
      if (response.data.success && response.data.data) {
        const formattedSubcategories = response.data.data.map(sub => ({
          id: sub.id.toString(),
          originalId: sub.id,
          label: sub.nom,
          description: sub.description,
          categoryId: categoryId,
          categoryName: response.data.parentCategory?.nom || 'Catégorie'
        }));
        
        setSubcategories(prev => ({
          ...prev,
          [categoryId]: formattedSubcategories
        }));
      }
    } catch (error) {
      console.error(`Erreur lors du chargement des sous-catégories:`, error);
      setSubcategories(prev => ({
        ...prev,
        [categoryId]: []
      }));
    } finally {
      setLoadingSubcategories(prev => ({ ...prev, [categoryId]: false }));
    }
  };

  const toggleCategoryExpansion = (categoryId, originalId) => {
    if (expandedCategories.includes(categoryId)) {
      setExpandedCategories(expandedCategories.filter(id => id !== categoryId));
    } else {
      setExpandedCategories([...expandedCategories, categoryId]);
      if (!subcategories[originalId]) {
        fetchSubcategories(originalId);
      }
    }
  };

  const navigationItems = [
    { 
      label: 'Accueil', 
      path: '/homepage-dashboard', 
      icon: 'Home',
      iconType: 'fontawesome',
      tooltip: 'Accueil'
    },
    { 
      label: 'Boutique', 
      path: '/shop', 
      icon: 'ShoppingBag',
      iconType: 'fontawesome',
      tooltip: 'Boutique',
      badge: cartCount > 0
    },
    { 
      label: 'Vendre', 
      path: '/create-listing', 
      icon: 'Plus',
      iconType: 'fontawesome',
      tooltip: 'Créer une annonce'
    },
    { 
      label: 'Enchères', 
      path: '/encheres', 
      icon: 'Gavel',
      iconType: 'fontawesome',
      tooltip: 'Enchères',
      badge: auctionCount > 0
    },
  ];

  const accountItems = [
    { 
      label: 'Mon Profil', 
      path: '/user-profile', 
      icon: 'User',
      iconType: 'fontawesome',
      description: 'Gérer votre compte'
    },
    { 
      label: 'Mes Favoris', 
      path: '/favoris', 
      icon: 'Heart',
      iconType: 'fontawesome',
      description: 'Vos pièces favorites'
    },
    { 
      label: 'Paramètres', 
      path: '/settings', 
      icon: 'Settings',
      iconType: 'fontawesome',
      description: 'Préférences du compte'
    }
  ];

  const handleSubcategoryPress = (subcategory) => {
    navigate('/rechercher', { 
      state: {
        query: null,
        categoryId: null,
        categoryName: null,
        subcategoryId: subcategory.originalId,
        subcategoryName: subcategory.label,
        originalCategoryId: subcategory.categoryId
      }
    });
    setIsCategoriesModalOpen(false);
    setActiveCategory(null);
    setIsMobileMenuOpen(false);
  };

  const handleCategoryNavigation = (category) => {
    navigate('/rechercher', { 
      state: {
        query: null,
        categoryId: category.originalId,
        categoryName: category.label
      }
    });
    setIsCategoriesModalOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setIsAccountDropdownOpen(false);
    setIsCategoriesModalOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    setIsAuthenticated(false);
    setUserData(null);
    setIsAccountDropdownOpen(false);
    setIsCategoriesModalOpen(false);
    
    // Émettre un événement pour informer les autres composants
    window.dispatchEvent(new Event('auth-change'));
    
    navigate('/auth');
  };

  const isActivePath = (path) => {
    if (path === '/homepage-dashboard' && location?.pathname === '/') return true;
    return location?.pathname === path;
  };

  const renderCategoryIcon = (category) => {
    if (category.photo_url) {
      return (
        <img 
          src={category.photo_url} 
          alt={category.label}
          className="w-10 h-10 rounded-lg object-cover"
        />
      );
    }
    return (
      <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
        <Icon name={category.icon} size={18} color="white" />
      </div>
    );
  };

  const renderCategoriesModal = () => (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 animate-fade-in" 
         onClick={() => setIsCategoriesModalOpen(false)}>
      <div 
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-xl shadow-xl animate-slide-up max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle Bar */}
        <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-4"></div>
        
        {/* Header */}
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Catégories</h2>
          <button
            onClick={() => setIsCategoriesModalOpen(false)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Icon name="X" size={20} color="#64748b" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loadingCategories ? (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mb-3"></div>
              <p className="text-gray-600">Chargement des catégories...</p>
            </div>
          ) : apiCategories.length > 0 ? (
            <div className="space-y-3">
              {apiCategories.map((category) => {
                const isExpanded = expandedCategories.includes(category.id);
                const categorySubcategories = subcategories[category.originalId] || [];
                
                return (
                  <div key={category.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className={`flex items-center justify-between p-4 ${isExpanded ? 'border-b border-gray-200' : ''}`}>
                      <div className="flex items-center space-x-3 flex-1">
                        {renderCategoryIcon(category)}
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{category.label}</h3>
                          {category.description && (
                            <p className="text-sm text-gray-500 truncate">{category.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCategoryNavigation(category);
                          }}
                          className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                          title="Rechercher dans cette catégorie"
                        >
                          <Icon name="Search" size={14} color="white" />
                        </button>
                        <button
                          onClick={() => toggleCategoryExpansion(category.id, category.originalId)}
                          className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={20} />
                        </button>
                      </div>
                    </div>
                    
                    {/* Subcategories */}
                    {isExpanded && (
                      <div className="bg-gray-50 p-4">
                        <div className="mb-3">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Sous-catégories {categorySubcategories.length > 0 && `(${categorySubcategories.length})`}
                          </h4>
                        </div>
                        
                        {loadingSubcategories[category.originalId] ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500 mr-2"></div>
                            <p className="text-sm text-gray-600">Chargement...</p>
                          </div>
                        ) : categorySubcategories.length > 0 ? (
                          <div className="space-y-2">
                            {categorySubcategories.map((subcategory) => (
                              <button
                                key={subcategory.id}
                                onClick={() => handleSubcategoryPress(subcategory)}
                                className="w-full flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-orange-300 hover:shadow-sm transition-all group"
                              >
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center group-hover:bg-orange-50 transition-colors">
                                    <Icon name="ChevronRight" size={14} color="#64748b" />
                                  </div>
                                  <div className="text-left">
                                    <p className="font-medium text-gray-900">{subcategory.label}</p>
                                    {subcategory.description && subcategory.description !== subcategory.label && (
                                      <p className="text-xs text-gray-500 line-clamp-1">{subcategory.description}</p>
                                    )}
                                  </div>
                                </div>
                                <Icon name="ArrowRight" size={16} color="#cbd5e1" />
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center py-6">
                            <Icon name="Folder" size={24} color="#cbd5e1" />
                            <p className="text-gray-500 text-sm mt-2">Aucune sous-catégorie disponible</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10">
              <Icon name="AlertCircle" size={48} color="#cbd5e1" />
              <p className="text-gray-600 mt-3 mb-4">Aucune catégorie disponible</p>
              <button
                onClick={fetchCategories}
                className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                <Icon name="RefreshCw" size={16} color="white" />
                <span>Réessayer</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderMobileMenuModal = () => (
    <div className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-40 animate-fade-in" 
         onClick={() => setIsMobileMenuOpen(false)}>
      <div 
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-xl shadow-xl animate-slide-up max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle Bar */}
        <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-4"></div>
        
        {/* Header */}
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Navigation</h2>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Icon name="X" size={20} color="#64748b" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* User Info if authenticated */}
          {isAuthenticated && userData && (
            <div className="mb-6 p-4 bg-orange-50 rounded-xl border border-orange-100">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center">
                  <Icon name="User" size={24} color="white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">{userData.nom || 'Utilisateur'}</h3>
                  <p className="text-sm text-gray-600">{userData.email || userData.telephone}</p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Items */}
          <div className="space-y-2 mb-6">
            {navigationItems?.map((item) => {
              const isActive = isActivePath(item.path);
              return (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`w-full flex items-center p-4 rounded-xl transition-all duration-200 text-left ${
                    isActive 
                      ? 'bg-orange-50 border border-orange-200 text-orange-700' 
                      : 'border border-gray-200 hover:bg-gray-50 text-gray-900'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isActive ? 'bg-orange-100' : 'bg-gray-100'
                    }`}>
                      <Icon name={item.icon} size={20} color={isActive ? '#f68a3b' : '#666'} />
                    </div>
                    <span className="font-semibold">{item?.label}</span>
                  </div>
                  {item.badge && (
                    <span className="ml-auto bg-orange-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                      {item.path === '/shop' ? cartCount : auctionCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Categories Button */}
          <button
            onClick={() => {
              setIsMobileMenuOpen(false);
              setIsCategoriesModalOpen(true);
            }}
            className="w-full flex items-center justify-between p-4 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl hover:bg-blue-100 transition-colors mb-6"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Icon name="Tag" size={20} color="#3b82f6" />
              </div>
              <div className="text-left">
                <p className="font-semibold">Catégories</p>
                <p className="text-sm">Parcourir toutes les catégories</p>
              </div>
            </div>
            <Icon name="ChevronRight" size={20} color="#3b82f6" />
          </button>

          {/* Si connecté, montrer les options de compte */}
          {isAuthenticated ? (
            <>
              {/* Account Section */}
              <div className="mb-6">
                <div className="flex items-center px-4 py-3 bg-gray-100 rounded-xl mb-4">
                  <Icon name="User" size={16} color="#6b7280" />
                  <span className="ml-3 font-semibold text-gray-800">Mon Compte</span>
                </div>
                
                <div className="space-y-3">
                  {accountItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => handleNavigation(item.path)}
                      className="w-full flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center">
                          <Icon name={item.icon} size={18} color="#64748b" />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold text-gray-900">{item.label}</p>
                          <p className="text-sm text-gray-500">{item.description}</p>
                        </div>
                      </div>
                      <Icon name="ChevronRight" size={20} color="#94a3b8" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center space-x-2 py-4 px-4 bg-red-50 border border-red-200 text-red-600 rounded-xl hover:bg-red-100 transition-colors"
              >
                <Icon name="LogOut" size={20} color="#ef4444" />
                <span className="font-semibold">Déconnexion</span>
              </button>
            </>
          ) : (
            /* Si non connecté, montrer les boutons de connexion/inscription */
            <>
              <div className="space-y-3 mb-6">
                <button
                  onClick={() => handleNavigation('/auth')}
                  className="w-full flex items-center justify-center p-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors"
                >
                  <span className="font-semibold">Se connecter</span>
                </button>
                
                <button
                  onClick={() => handleNavigation('/auth')}
                  className="w-full flex items-center justify-center p-4 border-2 border-orange-500 text-orange-600 rounded-xl hover:bg-orange-50 transition-colors"
                >
                  <span className="font-semibold">S'inscrire</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Header */}
      <header className="sticky top-0 z-40 w-full bg-white border-b border-gray-200 shadow-sm">
        <div className="flex h-16 items-center px-4 lg:px-6">
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer"
            onClick={() => handleNavigation('/homepage-dashboard')}
          >
            <div className="flex items-center space-x-2">
             <img src="/assets/images/AutoCass1.png" alt="Logo autocasses" style={{width: 150}} />
            </div>
          </div>

          {/* Counter Badge */}
          <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/5 top-0">
            <div className="flex items-center px-3 py-1 bg-orange-500 text-white rounded-b-lg shadow-md">
              <Icon name="Users" size={12} color="white" />
              <span className="ml-1 text-sm font-bold">165,000</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 ml-8">
            {navigationItems?.map((item) => (
              <Button
                key={item?.path}
                variant={isActivePath(item?.path) ? "default" : "ghost"}
                size="sm"
                onClick={() => handleNavigation(item?.path)}
                iconName={item.icon}
                iconPosition="left"
                iconSize={16}
                className="transition-all duration-200"
                title={item?.tooltip}
              >
                {item?.label}
              </Button>
            ))}
            
            {/* Categories Button (Desktop) */}
            <Button
              variant="outline"
              size="sm"
              iconName="Menu"
              onClick={() => setIsCategoriesModalOpen(true)}
              iconPosition="left"
              iconSize={16}
              className="ml-2 border-orange-500 text-orange-600 hover:bg-orange-50"
            >
              Catégories
            </Button>
          </nav>

          {/* Right Side Actions */}
          <div className="ml-auto flex items-center space-x-4">
            {/* Favorites Button */}
            <Button
              variant="ghost"
              size="sm"
              iconName="Heart"
              onClick={() => handleNavigation('/favoris')}
              iconPosition="left"
              iconSize={16}
              className="relative"
            >
              <span className="hidden lg:inline">Favoris</span>
              {favoritesCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {favoritesCount}
                </span>
              )}
            </Button>

            {/* Mobile Menu Toggle */}
            <Button
              variant="default"
              size="sm"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              iconName="Menu"
              iconSize={20}
              className="md:hidden bg-orange-500 hover:bg-orange-600"
            >
              Menu
            </Button>

            {/* Vérifier l'authentification */}
            {isAuthenticated ? (
              <>
                {/* Profile Button for Desktop */}
                <div className="hidden md:flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                      <Icon name="User" size={16} color="#f68a3b" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">
                      {userData?.nom?.split(' ')[0] || 'Profil'}
                    </span>
                  </div>
                  
                  {/* Account Dropdown (Desktop) */}
                  <div className="relative">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
                      iconName="Settings"
                      iconPosition="left"
                      iconSize={16}
                      className="transition-all duration-200"
                    >
                      <Icon 
                        name={isAccountDropdownOpen ? "ChevronUp" : "ChevronDown"} 
                        size={16} 
                        className="ml-1 transition-transform duration-200"
                      />
                    </Button>
                    {isAccountDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 animate-fade-in">
                        <div className="p-2">
                          {accountItems?.map((item) => (
                            <button
                              key={item?.path}
                              onClick={() => handleNavigation(item?.path)}
                              className="w-full flex items-start p-3 rounded-md hover:bg-gray-100 transition-all duration-200 text-left"
                            >
                              <Icon name={item?.icon} size={16} className="mt-0.5 mr-3 text-gray-500" />
                              <div>
                                <div className="text-sm font-medium text-gray-900">{item?.label}</div>
                                <div className="text-xs text-gray-500">{item?.description}</div>
                              </div>
                            </button>
                          ))}
                          <div className="border-t border-gray-200 mt-2 pt-2">
                            <button
                              onClick={handleLogout}
                              className="w-full flex items-center p-3 rounded-md hover:bg-red-50 transition-all duration-200 text-red-600"
                            >
                              <Icon name="LogOut" size={16} className="mr-3" />
                              <span className="text-sm font-medium">Déconnexion</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="hidden md:flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleNavigation('/auth')}
                >
                  Connexion
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleNavigation('/auth')}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Inscription
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>
      
      {/* Mobile Bottom Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 shadow-lg">
        <div className="flex items-center justify-around h-[70px] pb-2 pt-1">
          {navigationItems?.map((item) => {
            const isActive = isActivePath(item.path);
            return (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`flex flex-col items-center justify-center p-2 rounded-lg transition-all duration-200 relative ${
                  isActive 
                    ? 'text-orange-600' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <div className="relative">
                  <Icon name={item.icon} size={22} color={isActive ? '#f68a3b' : '#666'} />
                  {item.badge && (
                    <span 
                      className={`absolute -top-1 -right-1 ${
                        item.path === '/encheres' 
                          ? 'bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold'
                          : 'bg-orange-500 text-white text-[7px] rounded-full w-4 h-4 flex items-center justify-center'
                      }`}
                    >
                      {item.path === '/shop' ? cartCount : auctionCount}
                    </span>
                  )}
                </div>
                <span className={`text-xs font-bold mt-1 ${isActive ? 'text-orange-600' : 'text-gray-600'}`}>
                  {item.label}
                </span>
                {isActive && (
                  <div className="absolute top-0 w-10 h-1 bg-orange-600 rounded-full"></div>
                )}
              </button>
            );
          })}
          
          {/* Categories Button for Mobile Bottom Nav */}
          <button
            onClick={() => setIsCategoriesModalOpen(true)}
            className={`flex flex-col items-center justify-center p-2 rounded-lg transition-all duration-200 relative ${
              location?.pathname === '/categories' 
                ? 'text-orange-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="relative">
              <Icon name="Tag" size={22} color={location?.pathname === '/categories' ? '#f68a3b' : '#666'} />
            </div>
            <span className={`text-xs font-bold mt-1 ${location?.pathname === '/categories' ? 'text-orange-600' : 'text-gray-600'}`}>
              Catégories
            </span>
            {location?.pathname === '/categories' && (
              <div className="absolute top-0 w-10 h-1 bg-orange-600 rounded-full"></div>
            )}
          </button>
        </div>
      </div>
      
      {/* Modals */}
      {isCategoriesModalOpen && renderCategoriesModal()}
      {isMobileMenuOpen && renderMobileMenuModal()}
      
      {/* Padding for bottom navigation */}
      <div className="md:hidden pb-[70px]"></div>
      
      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }
        
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </>
  );
};

export default Header;